import { useState, useCallback } from 'react';
import styled from '@emotion/styled';
import { tokens, Text, DateRangePicker, Link } from '@hotjar/hotsauce';

import type { AbsoluteDateRange, DateRange, MinimalDateConstraint } from './DateRangeFilterType';
import { DATE_RANGE_TYPE } from './DateRangeFilterType';
import { GenericFilterFooter } from '../GenericBloc/GenericFilter/GenericFilterFooter';
import { GenericFilterBody } from '../GenericBloc/GenericFilter/GenericFilterBody';
import { GenericFilterDropdown } from '../GenericBloc/GenericFilter/GenericFilterDropdown';
import { GenericFilterFooterMessage } from '../GenericBloc/GenericFilter/GenericFilterFooterMessage';
import type { AbstractSingleFilterDropdownProps } from '../SingleFilterType';
import { makeAbsoluteDateRange } from './DateRangeFilterUtils';
import { validateValue, ValidationErrorEnum } from './DateRangeFilterValidator';

const ErrorContainer = styled.div`
  padding-top: ${tokens.spacer.x1};
`;

interface DateRangeDropdownProps extends AbstractSingleFilterDropdownProps<DateRange> {
  dateConstraint?: MinimalDateConstraint;
}

export function CustomDateRangeDropdown({
  currentValue,
  dateConstraint,
  onApply,
  filterName,
}: DateRangeDropdownProps): JSX.Element {
  const [dirtyCustomDateRange, setDirtyCustomDateRange] = useState<AbsoluteDateRange | undefined>(
    currentValue?.type === DATE_RANGE_TYPE.ABSOLUTE ? currentValue : undefined,
  );
  const [validationError, setValidationError] = useState<ValidationErrorEnum>();
  const handleCustomDateChange = useCallback(
    (newValue) => {
      if (newValue != null) {
        setDirtyCustomDateRange(makeAbsoluteDateRange(newValue.from, newValue.to));
        setValidationError(undefined);
      }
    },
    [setDirtyCustomDateRange, setValidationError],
  );

  const handleApply = useCallback(() => {
    const optionalError = dirtyCustomDateRange
      ? validateValue(dirtyCustomDateRange, dirtyCustomDateRange, dateConstraint?.minDate)
      : ValidationErrorEnum.INVALID_CUSTOM_DATE;
    if (optionalError) {
      setValidationError(optionalError);
    } else if (dirtyCustomDateRange) {
      // If `from` is after `to` or vice versa, we swap them
      const from =
        dirtyCustomDateRange.from < dirtyCustomDateRange.to
          ? dirtyCustomDateRange.from
          : dirtyCustomDateRange.to;
      const to =
        dirtyCustomDateRange.from < dirtyCustomDateRange.to
          ? dirtyCustomDateRange.to
          : dirtyCustomDateRange.from;

      onApply(makeAbsoluteDateRange(from, to));
    }
  }, [dirtyCustomDateRange, dateConstraint, onApply]);

  let contraintToShowInFooter = currentValue?.autoSelectedConstraint;
  if (!contraintToShowInFooter && dateConstraint?.isFilterRelated === true) {
    contraintToShowInFooter = dateConstraint;
  }
  const footerMessage = contraintToShowInFooter ? (
    <GenericFilterFooterMessage>
      <span>{contraintToShowInFooter.reason} </span>
      {contraintToShowInFooter.link && (
        <Link kind="inline" href={contraintToShowInFooter.link.href} isExternal>
          {contraintToShowInFooter.link.text}
        </Link>
      )}
    </GenericFilterFooterMessage>
  ) : null;

  return (
    <GenericFilterDropdown
      ariaLabel={`${filterName} dropdown`}
      customWidth={370}
      onApply={handleApply}
    >
      <GenericFilterBody>
        <DateRangePicker
          range={dirtyCustomDateRange}
          onDateChange={handleCustomDateChange}
          fromIsInvalid={
            validationError === ValidationErrorEnum.INVALID_CUSTOM_DATE ||
            validationError === ValidationErrorEnum.INVALID_CUSTOM_FROM_DATE ||
            validationError === ValidationErrorEnum.FROM_DATE_BEFORE_MIN_DATE
          }
          toIsInvalid={
            validationError === ValidationErrorEnum.INVALID_CUSTOM_DATE ||
            validationError === ValidationErrorEnum.INVALID_CUSTOM_TO_DATE ||
            validationError === ValidationErrorEnum.TO_DATE_BEFORE_MIN_DATE
          }
          preventFutureDate
          preventDatesBefore={dateConstraint?.minDate}
        />
        {(validationError === ValidationErrorEnum.INVALID_CUSTOM_DATE ||
          validationError === ValidationErrorEnum.INVALID_CUSTOM_FROM_DATE ||
          validationError === ValidationErrorEnum.INVALID_CUSTOM_TO_DATE ||
          validationError === ValidationErrorEnum.FROM_DATE_BEFORE_MIN_DATE ||
          validationError === ValidationErrorEnum.TO_DATE_BEFORE_MIN_DATE) && (
          <ErrorContainer>
            <Text textColor="text_error">
              {validationError === ValidationErrorEnum.FROM_DATE_BEFORE_MIN_DATE ||
              validationError === ValidationErrorEnum.TO_DATE_BEFORE_MIN_DATE
                ? dateConstraint?.reason
                : validationError}
            </Text>
          </ErrorContainer>
        )}
      </GenericFilterBody>
      <GenericFilterFooter footerMessage={footerMessage} />
    </GenericFilterDropdown>
  );
}
